/**
 * High level router.
 *
 * Note: It's recommended to compose related routes in internal router
 * components (e.g: `src/pages/auth/AuthPage`, `src/pages/home/RoleBasedGuard`).
 */

import React from "react";
import { JssProvider } from "react-jss";
import { shallowEqual, useSelector } from "react-redux";
import {
	Redirect,
	Route,
	Switch,
	useLocation,
	withRouter
} from "react-router-dom";
import { useLastLocation } from "react-router-last-location";
import { LayoutContextProvider } from "../../_metronic";
import Layout from "../../_metronic/layout/Layout";
import RoleBasedGuard from "../pages/admin/RoleBasedGuard";
import AuthPage from "../pages/auth/AuthPage";
import LogoutPage from "../pages/auth/Logout";
import ErrorPage from "../pages/errors/ErrorPage";
import HomePage from "../pages/home";
import * as routerHelpers from "../router/RouterHelpers";
import { generateClassName } from "../utils/generateClassName";
import MaintenancePage from "../pages/maintenance";
import { useMaintenanceMode } from "../hooks/useMaintenanceMode";
import { TaskInfoPage } from "../pages/admin/pages/TaskInfo";


export const Routes = withRouter(({ history }) => {
	const lastLocation = useLastLocation();
	const location = useLocation();

	routerHelpers.saveLastLocation(lastLocation);
	const { isAuthorized, menuConfig, userLastLocation } = useSelector(
		({ auth, builder: { menuConfig } }) => ({
			menuConfig,
			isAuthorized: !!auth?.user,
			userLastLocation: routerHelpers.getLastLocation()
		}),
		shallowEqual
	);
	const isMaintenanceMode = useMaintenanceMode();
	if (isMaintenanceMode && !["/maintenance", "/auth/login"].includes(location.pathname)) {
		window.location.href = "/maintenance";
	}

	return (
		/* Create `LayoutContext` from current `history` and `menuConfig`. */
		<LayoutContextProvider history={history} menuConfig={menuConfig}>
			<Switch>
				<Route
					path="/admin"
					component={() => {
						window.location.href = `${process.env.REACT_APP_API_HOST}/admin/`;
						return null;
					}}
				/>
				<Route path="/auth" component={AuthPage} />
				<Route path="/auth/login" component={AuthPage} />
				<Route path="/logout" component={LogoutPage} />
				<Route path="/maintenance" component={MaintenancePage} />
				{isAuthorized ? (
					<>
						<Route exact path="/" component={HomePage} />
						<Route exact path="/landing" component={HomePage} />
						<Route path="/task/:uuid">
							<Layout history={history}>
								<JssProvider generateClassName={generateClassName}>
									<TaskInfoPage />
								</JssProvider>
							</Layout>
						</Route>
						<Route path="/dashboard">
							<Layout history={history}>
								<JssProvider generateClassName={generateClassName}>
									<RoleBasedGuard
										userLastLocation={userLastLocation}
										location={location}
										history={history}
									/>
								</JssProvider>
							</Layout>
						</Route>
					</>
				) : (
					<>
						<Redirect from="/dashboard/projects" to="/auth/login" />
						<Redirect from="/landing" to="/auth/login" />
					</>
				)}

				<Route exact path="*" component={ErrorPage} />
			</Switch>
		</LayoutContextProvider>
	);
});
